import {
  useCombobox,
  UseComboboxProps,
  UseComboboxState,
  UseComboboxStateChangeOptions,
} from "downshift";
import {
  MultiSelectFieldGroupByValue,
  MultiSelectFieldOption,
  MultiSelectFieldPinnedOptions,
} from "../types";
import { SelectItem } from "../../../../internal/types/selectFieldInternalTypes";
import { CheckState } from "../../../../types";
import { useMemo, useState } from "react";
import { useDownshiftEnvironment } from "../../../../internal/hooks";
import { useProcessedOptions } from "../../../../internal/hooks/useProcessedOptions";
import { buildSelectItems } from "../../../../internal/functions/buildSelectItems";
import { useToggleSelection } from "../../../../internal/hooks/useToggleSelection";

export type UseComboMultipleOptions = {
  /** An array of currently loaded options */
  options: MultiSelectFieldOption[];
  /** Options to pin to the top of the list */
  pinned?: MultiSelectFieldPinnedOptions;
  /** Function to convert a group value to a display label */
  groupToString?: (groupValue: MultiSelectFieldGroupByValue) => string;
  /** Function to sort group sections */
  groupSorter?: (
    a: MultiSelectFieldGroupByValue,
    b: MultiSelectFieldGroupByValue,
  ) => number;
  /** Configuration for the "Select All" option (shown when search is empty) */
  selectAll?: {
    label: string;
    onClick: () => void;
    checkState: CheckState;
  };
  /** Function that receives the search value and returns config for "Select Filtered" */
  selectFiltered?: (searchValue: string) => {
    label?: string;
    onClick: () => void;
    checkState: CheckState;
  };
  /** The currently selected options (controlled from outside) */
  selectedOptions: MultiSelectFieldOption[];
  onSelectedOptionsChange: (options: MultiSelectFieldOption[]) => void;
  displayAs: "popover" | "dialog";
  disabled: boolean;
  /** Whether options are currently loading (hides select-filtered to prevent stale selections) */
  loading?: boolean;
  /** Whether the list is virtualized (disables built-in scroll behavior) */
  virtualize?: boolean;
  onScrollToHighlight?: (index: number) => void;
} & Pick<UseComboboxProps<SelectItem>, "onInputValueChange" | "onIsOpenChange">;

/**
 * Hook that wraps downshift's useCombobox for multi-select behavior.
 * Unlike useCombo, this hook:
 * - Keeps the menu open after item selection
 * - Does not track internal selection state (controlled externally)
 * - Preserves input value on item selection for continued filtering
 * @param options - Configuration options for the hook
 * @returns Downshift combobox props and state
 */
export const useComboMultiple = ({
  options,
  pinned,
  groupToString,
  groupSorter,
  selectAll,
  selectFiltered,
  selectedOptions,
  onSelectedOptionsChange,
  onInputValueChange,
  onIsOpenChange,
  displayAs = "popover",
  disabled,
  loading = false,
  virtualize = false,
  onScrollToHighlight,
}: UseComboMultipleOptions) => {
  const [inputValue, setInputValue] = useState("");

  const { pinnedSections, ungroupedItems, groupSections } = useProcessedOptions(
    {
      options,
      pinned,
      searchValue: inputValue,
      groupToString,
      groupSorter,
    },
  );

  // Compute bulk action items (select-all or select-filtered, mutually exclusive)
  const bulkActionItems = useMemo(() => {
    const items: SelectItem[] = [];
    const hasSearchTerm = inputValue.trim().length > 0;
    const hasResults =
      ungroupedItems.length > 0 ||
      groupSections.some((s) => s.options.length > 0) ||
      pinnedSections.some((s) => s.options.length > 0);

    if (hasSearchTerm && selectFiltered && hasResults && !loading) {
      const config = selectFiltered(inputValue);
      items.push({
        id: "select-filtered",
        type: "select-filtered",
        original: {
          id: "select-filtered",
          label: config.label ?? `Select items matching "${inputValue}"`,
        },
        checkState: config.checkState,
      });
    } else if (!hasSearchTerm && selectAll) {
      items.push({
        id: "select-all",
        type: "select-all",
        original: {
          id: "select-all",
          label: selectAll.label ?? "Select All",
        },
        checkState: selectAll.checkState,
      });
    }
    return items;
  }, [
    inputValue,
    ungroupedItems,
    groupSections,
    pinnedSections,
    selectFiltered,
    selectAll,
    loading,
  ]);

  const { items, sectionsMeta } = useMemo(
    () =>
      buildSelectItems({
        prefixItems: bulkActionItems,
        pinnedSections,
        groupSections,
        ungroupedItems,
      }),
    [bulkActionItems, pinnedSections, groupSections, ungroupedItems],
  );

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const { handleItemClick } = useToggleSelection({
    options,
    pinnedSections,
    selectedOptions,
    onSelectedOptionsChange,
  });

  const {
    isOpen,
    highlightedIndex,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    inputValue: downshiftInputValue,
    closeMenu,
    openMenu,
    toggleMenu,
  } = useCombobox({
    isItemDisabled: (item, _index) => {
      if (!item) {
        return false; // Downshift passes -1 index with an undefined item. Ignore it.
      }
      return (disabled || item.disabled) ?? false;
    },
    environment: environment,
    // For multi-select, we don't track a single selected item
    selectedItem: null,
    onSelectedItemChange: ({
      selectedItem: si,
    }: {
      selectedItem: SelectItem | null;
    }) => {
      if (si) {
        if (si.type === "select-all") {
          selectAll?.onClick();
        } else if (si.type === "select-filtered") {
          selectFiltered?.(inputValue).onClick();
        } else if (
          si.type === "option" ||
          si.type === "pinned-option" ||
          si.type === "grouped-option"
        ) {
          handleItemClick(si.original);
        }
      }
    },
    onHighlightedIndexChange: ({ type, highlightedIndex: idx }) => {
      if (type !== useCombobox.stateChangeTypes.ItemMouseMove && idx >= 0) {
        onScrollToHighlight?.(idx);
      }
    },
    onInputValueChange: (changes) => {
      // Update local input value for pinned options search
      setInputValue(changes.inputValue ?? "");
      // Call the external handler
      onInputValueChange?.(changes);
    },
    scrollIntoView: virtualize
      ? () => {}
      : (node, menuNode) => {
          if (!node || !menuNode) return;

          const firstItem = items[0];
          const hasBulkAction =
            firstItem?.type === "select-all" ||
            firstItem?.type === "select-filtered";

          if (!hasBulkAction) {
            const nodeRect = node.getBoundingClientRect();
            const menuRect = menuNode.getBoundingClientRect();
            if (
              nodeRect.bottom > menuRect.bottom ||
              nodeRect.top < menuRect.top
            ) {
              node.scrollIntoView({ block: "nearest" });
            }
            return;
          }

          const bulkActionEl = menuNode.querySelector(
            '[data-type="select-all"], [data-type="select-filtered"]',
          );
          if (node === bulkActionEl) {
            menuNode.scrollTop = 0;
            return;
          }

          const stickyHeight =
            bulkActionEl?.getBoundingClientRect().height ?? 0;
          const nodeRect = node.getBoundingClientRect();
          const menuRect = menuNode.getBoundingClientRect();
          const visibleTop = menuRect.top + stickyHeight;

          if (nodeRect.top < visibleTop) {
            menuNode.scrollTop -= visibleTop - nodeRect.top;
          } else if (nodeRect.bottom > menuRect.bottom) {
            menuNode.scrollTop += nodeRect.bottom - menuRect.bottom;
          }
        },
    onIsOpenChange,
    items: items,
    itemToString(item: SelectItem | null) {
      return item?.original.label ?? "";
    },
    stateReducer: (state, actionAndChanges) =>
      stateReducer(state, actionAndChanges, displayAs),
  });

  return {
    inputValue: downshiftInputValue,
    wrapperDivRef,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
    isOpen,
    items,
    sectionsMeta,
    closeMenu,
    openMenu,
    toggleMenu,
    selectedOptions,
  };
};

function stateReducer(
  state: UseComboboxState<SelectItem>,
  actionAndChanges: UseComboboxStateChangeOptions<SelectItem>,
  displayAs: "popover" | "dialog",
) {
  const { type, changes } = actionAndChanges;

  switch (type) {
    case useCombobox.stateChangeTypes.InputBlur:
      // keep the dialog open if the user blurs the input while the dialog is open
      if (displayAs === "dialog" && state.isOpen) {
        return {
          ...changes,
          isOpen: state.isOpen,
          // Preserve input value for multi-select
          inputValue: state.inputValue,
        };
      }
      return {
        ...changes,
        // For multi-select, preserve the input value on blur
        inputValue: state.inputValue,
      };
    case useCombobox.stateChangeTypes.InputKeyDownEscape:
    case useCombobox.stateChangeTypes.FunctionCloseMenu:
      return {
        ...changes,
        // Clear input value when menu is closed
        inputValue: "",
      };
    case useCombobox.stateChangeTypes.InputClick:
      if (displayAs === "dialog" && state.isOpen) {
        // keep the dialog open if the user clicks the input while the dialog is open
        return {
          ...changes,
          isOpen: state.isOpen,
        };
      }
      return changes;
    case useCombobox.stateChangeTypes.InputKeyDownEnter:
    case useCombobox.stateChangeTypes.ItemClick:
      // Optimize for continuing to select items based on the input value
      return {
        ...changes,
        isOpen: state.isOpen, // Keep menu open
        highlightedIndex: state.highlightedIndex, // Preserve highlight position
        inputValue: state.inputValue, // Preserve input value for continued filtering
      };
    case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
      return {
        ...changes,
        inputValue: state.inputValue,
      };
    default:
      return changes;
  }
}
